/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
} from '@appblocks/codegeneration/types';
import BaseNode from '@appblocks/codegeneration/Nodes/BaseNode';
import NodePort from '@appblocks/codegeneration/Nodes/NodePort';
import NodeParameter from '@appblocks/codegeneration/Nodes/NodeParameter';
import PlatformUtils from '@appblocks/platforms/PlatformUtils';

export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    let blocks: Array<CodeBlock> = [];
    const REGISTERS = feature.getSettingValue('REGISTERS');
    const NAME = feature.getSettingValue('NAME');
    const DEVICEID = feature.getSettingValue('DEVICEID');
    const { project } = feature;
    const PROTOCOL = feature.getSettingValue('PROTOCOL');
    let SERIAL_PORT;
    if (PROTOCOL === 'modbus_TCP') {
        SERIAL_PORT = `${NAME}_MODBUS_SOCK_NUM`;
    } else {
        const SERIAL_PORT_FEATURE = project.getFeature(feature.getSettingValue('SERIAL_PORT'));
        if (SERIAL_PORT_FEATURE) {
            SERIAL_PORT = `SER_${SERIAL_PORT_FEATURE.getSettingValue('NAME')}`;
        }
    }
    const INTERFACE = 'current_interface';
    for (let i = 0; i < REGISTERS.length; i += 1) {
        blocks = blocks.concat(REGISTERS[i].getCodeBlocks());
        const register = REGISTERS[i];
        blocks.push({
            location: 'MODBUS_POLL_SAMPLE',
            blockType: BlockType.CODE,
            template: `
    '${register.getSettingValue('NAME')}
    'modbus_master_request(${DEVICEID}, ${register.getSettingValue('DATATYPE')}, ${register.getSettingValue('ADDRESS')}, ${register.getSettingValue('LENGTH')}, "", MODBUS_MASTER_INTERFACE, ${PROTOCOL},0)`,
        });
    }

    blocks.push({
        location: 'GLOBAL_DECLARATIONS',
        blockType: BlockType.CODE,
        template: `
declare ${NAME}_DEVICE_ID as byte
`,
    });

    blocks.push({
        location: 'GLOBAL_DIMS',
        blockType: BlockType.CODE,
        template: `
dim ${NAME}_DEVICE_ID as byte
`,
    });

    blocks.push({
        location: 'boot',
        blockType: BlockType.CODE,
        template: `
${NAME}_DEVICE_ID=${project.parseCodeExpression(DEVICEID, [], TibboDataType.BYTE)}
`,
    });

    blocks.push({
        location: 'callback_modbus_slave_reply',
        blockType: BlockType.CODE,
        template: `
    if request_item.device_id=${NAME}_DEVICE_ID 
        AND request_item.protocol=${PROTOCOL}
        AND request_item.interface_number=${SERIAL_PORT} then
        '****${NAME}_DEVICE_ID****'
    end if
        `,
    });

    if (PROTOCOL === 'modbus_TCP') {
        blocks.push({
            location: 'GLOBAL_DECLARATIONS',
            blockType: BlockType.CODE,
            template: `
declare ${NAME}_MODBUS_SOCK_NUM as byte`,
        });

        blocks.push({
            location: 'GLOBAL_DIMS',
            blockType: BlockType.CODE,
            template: `
dim ${NAME}_MODBUS_SOCK_NUM as byte`,
        });

        blocks.push({
            location: 'MODBUS_DEVICE_SETUP',
            blockType: BlockType.CODE,
            template: `
    ${NAME}_MODBUS_SOCK_NUM=sock_get("MB${feature.getSettingValue('DEVICEID')}")
    sock.num=${NAME}_MODBUS_SOCK_NUM
    sock.protocol=PL_SOCK_PROTOCOL_TCP
    sock.inconmode=PL_SOCK_INCONMODE_NONE
    sock.targetinterface=${INTERFACE}
    sock.allowedinterfaces="NET,WLN,PPP"
    sock.txbuffrq(1)
    sock.rxbuffrq(1)
    sys.buffalloc()
    sock.targetip=${project.parseCodeExpression(feature.getSettingValue('DEVICEIP'), [], TibboDataType.STRING)}
    sock.targetport=${project.parseCodeExpression(feature.getSettingValue('DEVICEPORT'), [], TibboDataType.STRING)}
        `,
        });

        blocks.push({
            location: project.getNetworkConnectedLocation(INTERFACE),
            blockType: BlockType.CODE,
            template: `
    sock.num=${NAME}_MODBUS_SOCK_NUM
    sock.targetinterface=current_interface
    sock.connect()
        `,
        });

        blocks.push({
            location: 'on_sock_event',
            blockType: BlockType.CODE,
            template: `
    if sock.num=${NAME}_MODBUS_SOCK_NUM AND newstatesimple=PL_SSTS_CLOSED AND interface_ready(current_interface)=YES then
        sock.connect()
    end if
        `,
        });

        blocks.push({
            location: 'on_sock_data_arrival',
            blockType: BlockType.CODE,
            id: `MODBUS_RECV_${NAME}_MODBUS_SOCK_NUM`,
            template: `
if sock.num=${NAME}_MODBUS_SOCK_NUM then
    'modbus master data
    modbus_master_receive(sock.getdata(255),modbus_TCP)
end if`,
        });
    } else {
        blocks.push({
            location: 'on_ser_data_arrival',
            blockType: BlockType.CODE,
            id: `MODBUS_RECV_${SERIAL_PORT}`,
            template: `
if ser.num=${SERIAL_PORT} then
    'modbus master data
    modbus_master_receive(ser.getdata(255),modbus_RTU)
end if`,
        });
    }

    return blocks;
};
